import { ChevronDownIcon, CloseIcon, SearchIcon } from '@chakra-ui/icons'
import {
  Badge,
  Box,
  Button,
  Center,
  Checkbox,
  Divider,
  Flex,
  HStack,
  IconButton,
  Input,
  Portal,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react'
import { useScreen } from '@hb/react'
import {
  DropdownField,
  DropdownOption,
  DropdownOptionGroup,
  DropdownOptionItem,
  filterOptionsBySearch,
  getAllOptions,
  getOptionFromId,
  getOptionFromText,
  InputSize,
  isDropdownOptionItem,
} from '@hb/shared'
import useResizeObserver from '@react-hook/resize-observer'
import {
  CSSProperties,
  FC,
  forwardRef,
  KeyboardEvent,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { FieldRenderProps } from 'react-final-form'
import { InputElement, InputProps } from '../../../../types/fields'
import { useStatusBorderColor } from '../Shared/hooks'
import { getInputContainerPaddingX, getInputContainerPaddingY } from '../Shared/InputContainer'
import { getInputSizeTokens, useInputSize } from '../Shared/sizes'
import { baseInputStyle } from '../Shared/styles'
import { getScrollableParent } from './utils'

// Inner field height (Chakra spacing unit) of the dropdown, scaled by input size.
const dropdownFieldHeight = (size: InputSize): number => {
  switch (size) {
    case 'xs':
      return 3
    case 'sm':
      return 4
    case 'lg':
      return 6
    default:
      return 5
  }
}

const DropdownSearchBar = forwardRef<
  HTMLInputElement,
  {
    value?: string | string[]
    field: DropdownField
    meta: FieldRenderProps['meta']
    fieldActive?: boolean
    style?: CSSProperties
    searchQuery?: string
    name?: string
    onBlur: () => void
    onFocus: () => void
    onSearchQueryChange: (v: string) => void
    onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void
    previewText: string
    flipped?: boolean
    disabled?: boolean
    inputStyle?: CSSProperties
    size?: InputSize
    selectedItems?: DropdownOptionItem[]
  }
>(
  (
    {
      value,
      fieldActive,
      searchQuery,
      onSearchQueryChange,
      onBlur,
      name,
      field,
      meta,
      style,
      onFocus,
      onKeyDown,
      previewText,
      flipped,
      disabled,
      inputStyle,
      selectedItems,
      size: sizeProp,
    },
    ref,
  ) => {
    const { placeholder } = field ?? {}
    const size = useInputSize(sizeProp ?? field?.size)
    const sizeTokens = getInputSizeTokens(size)
    const borderColor = useStatusBorderColor(field, meta)
    const numSelected = selectedItems?.length ?? 0
    const showBadges = !fieldActive && numSelected > 1
    const hasValue = Array.isArray(value) ? value.length > 0 : !!value
    return (
      <HStack
        onClick={e => {
          e.stopPropagation()
          e.preventDefault()
        }}
        spacing={0}
        onMouseDown={e => e.stopPropagation()}
        onMouseUp={e => e.stopPropagation()}
        w="100%"
        py={getInputContainerPaddingY(size)}
        px={getInputContainerPaddingX(size)}
        bg="white"
        border="0.5px solid"
        borderColor={borderColor}
        borderTopRadius={flipped ? (fieldActive ? 0 : 8) : 8}
        fontSize={sizeTokens.fontSize}
        transition="all 500ms"
        borderBottomRadius={flipped ? 8 : fieldActive ? 0 : 8}
        style={style}>
        <Center
          width={fieldActive || !hasValue ? 'auto' : '0px'}
          pr={fieldActive || !hasValue ? sizeTokens.innerPaddingX : 0}
          transition="width 300ms, opacity 300ms"
          opacity={fieldActive || !hasValue ? 1 : 0}
          overflow="hidden">
          <SearchIcon position="relative" top="1px" color="gray.400" width="14px" />
        </Center>
        <Box flex={1} minH={dropdownFieldHeight(size)} position="relative" minW={0}>
          <Input
            autoComplete="off"
            ref={ref}
            w="100%"
            onBlur={onBlur}
            name={name}
            h={dropdownFieldHeight(size)}
            opacity={!showBadges ? 1 : 0}
            left={0}
            right={0}
            top={0}
            bg="red"
            transition="all 300ms"
            pl={fieldActive || !value ? 0 : 1}
            sx={{ ':focus': { boxShadow: 'none' } }}
            border="none"
            position="absolute"
            color={showBadges ? 'transparent' : 'gray.700'}
            onFocus={onFocus}
            onKeyDown={onKeyDown}
            style={{ ...baseInputStyle(size, disabled), ...inputStyle }}
            value={
              (fieldActive ? searchQuery : Array.isArray(value) ? value.join(', ') : value) ?? ''
            }
            placeholder={previewText || placeholder}
            onChange={({ target: { value: updated } }) => onSearchQueryChange(updated)}
          />
          {showBadges ? (
            <Flex flexWrap="wrap" gap={1} align="center" pointerEvents="none">
              {selectedItems
                ? selectedItems.map(item => (
                    <Badge key={item.id} colorScheme="green">
                      {item.text}
                    </Badge>
                  ))
                : placeholder}
            </Flex>
          ) : null}
        </Box>
      </HStack>
    )
  },
)

const DropdownItemMenuGroup: FC<{
  option: DropdownOptionGroup<any>
  field: DropdownField
  onChange: (v?: string) => void
  focusedIndex?: number
  selected: string | string[]
  baseIndex: number
  onFocusedElementRef?: (element: HTMLElement | null, index: number) => void
}> = ({
  option: { title, options },
  field,
  onChange,
  focusedIndex,
  selected,
  baseIndex,
  onFocusedElementRef,
}) => (
  <VStack align="flex-start" w="100%" spacing={0}>
    <Text
      w="100%"
      py={1}
      px={2}
      bg="gray.100"
      fontSize="sm"
      fontWeight={600}
      color="gray.600"
      borderBottom="1px solid #e2e8f0"
      userSelect="none"
      onPointerDown={e => {
        e.preventDefault()
        e.stopPropagation()
      }}
      onMouseDown={e => {
        e.preventDefault()
        e.stopPropagation()
      }}
      onClick={e => {
        e.preventDefault()
        e.stopPropagation()
      }}>
      {title}
    </Text>
    <VStack borderLeft="4px solid #888" spacing={0} w="100%" align="flex-start">
      {options.map((option, idx) => (
        <DropdownOptionView
          key={`${idx}`}
          option={option}
          field={field}
          onChange={onChange}
          index={idx}
          focusedIndex={focusedIndex}
          baseIndex={baseIndex + idx}
          onFocusedElementRef={onFocusedElementRef}
          selected={selected}
        />
      ))}
    </VStack>
  </VStack>
)

const DropdownOptionItemView: FC<{
  option: DropdownOptionItem
  field: DropdownField
  index: number
  onChange: (v: string) => void
  focusedIndex?: number
  baseIndex: number
  onFocusedElementRef?: (element: HTMLElement | null, index: number) => void
  selected: string | string[]
}> = ({
  option,
  onChange,
  field: { renderOption, allowMultiple },
  index: i,
  focusedIndex,
  baseIndex,
  onFocusedElementRef,
  selected,
}) => {
  const { id, text } = option
  const isFocused = focusedIndex === baseIndex

  const isSelected = Array.isArray(selected) ? selected.includes(id) : selected === id

  return (
    <Button
      ref={el => {
        if (isFocused && onFocusedElementRef) {
          onFocusedElementRef(el, baseIndex)
        }
      }}
      variant="unstyled"
      borderTop={i ? '1px solid #cdcdcd' : 'none'}
      key={id}
      aria-label={text}
      fontWeight={400}
      fontFamily="hero-new"
      w="100%"
      gap={1.5}
      cursor="pointer"
      _hover={{ bg: 'blackAlpha.100' }}
      // sx={{ ':nth-of-type(odd)': { bg: 'rgb(245,245,245)' } }}
      borderRadius="none"
      bg={isFocused ? 'blue.50' : 'transparent'}
      border={isFocused ? '1px solid' : 'none'}
      borderColor={isFocused ? 'blue.200' : 'transparent'}
      display="flex"
      textAlign="left"
      h="auto"
      py={renderOption ? 0 : 1.5}
      onBlur={e => {
        e.stopPropagation()
        e.preventDefault()
      }}
      px={renderOption ? 0 : 3}
      justifyContent="flex-start"
      whiteSpace="pre-wrap"
      onPointerDown={e => {
        e.preventDefault()
        e.stopPropagation()
      }}
      onMouseDown={e => {
        e.preventDefault()
        e.stopPropagation()
      }}
      onClick={e => {
        e.preventDefault()
        onChange(id)
      }}>
      {allowMultiple ? (
        <Checkbox
          colorScheme="green"
          isChecked={isSelected}
          onClick={e => e.stopPropagation()}
          onChange={() => onChange(id)}
        />
      ) : null}
      <Flex minW={0} flex={1}>
        {renderOption ? renderOption(option) : text}
      </Flex>
    </Button>
  )
}

const DropdownOptionView: FC<{
  option: DropdownOption<any>
  onChange: (v?: string) => void
  field: DropdownField
  index: number
  selected: string | string[]
  focusedIndex?: number
  baseIndex: number
  onFocusedElementRef?: (element: HTMLElement | null, index: number) => void
}> = ({
  option,
  onChange,
  field,
  index,
  focusedIndex,
  baseIndex,
  onFocusedElementRef,
  selected,
}) => {
  if (isDropdownOptionItem(option)) {
    return (
      <DropdownOptionItemView
        option={option}
        onChange={onChange}
        field={field}
        index={index}
        selected={selected}
        focusedIndex={focusedIndex}
        baseIndex={baseIndex}
        onFocusedElementRef={onFocusedElementRef}
      />
    )
  }
  return (
    <DropdownItemMenuGroup
      option={option}
      onChange={onChange}
      field={field}
      selected={selected}
      focusedIndex={focusedIndex}
      baseIndex={baseIndex}
      onFocusedElementRef={onFocusedElementRef}
    />
  )
}

export interface PopperPosition {
  x: number
  y: number
  height: number
  width: number
}
export const DropdownItemList: FC<{
  options: DropdownOption<any>[]
  field: DropdownField<any>
  onChange: (v: any | undefined) => void
  searchQuery?: string
  popperPosition: PopperPosition | null
  active: boolean
  selected: any | any[]
  focusedIndex?: number
  onFocusedElementRef?: (element: HTMLElement | null, index: number) => void
}> = ({
  options,
  field,
  onChange,
  searchQuery,
  selected,
  active,
  popperPosition,
  focusedIndex,
  onFocusedElementRef,
}) => {
  // Calculate base index for keyboard navigation
  const getBaseIndex = useCallback(
    (localIndex: number) => {
      let baseIndex = 0
      for (let i = 0; i < localIndex; i++) {
        const opt = options[i]
        if (isDropdownOptionItem(opt)) {
          baseIndex++
        } else {
          baseIndex += opt.options.length
        }
      }
      return baseIndex
    },
    [options],
  )

  return (
    <Portal>
      <VStack
        position="fixed"
        width={`${popperPosition?.width ?? 0}px`}
        top={`${(popperPosition?.y ?? 0) + (popperPosition?.height ?? 0)}px`}
        left={`${popperPosition?.x ?? 0}px`}
        zIndex={2}
        py={0}
        bg="white"
        align="flex-start"
        boxShadow="0 0 4px #00000077"
        minW="50px"
        borderBottomRadius={6}
        opacity={active ? 1 : 0}
        pointerEvents={active ? 'auto' : 'none'}
        transform={active ? 'translateY(0)' : 'translateY(-5px)'}
        transition="opacity 500ms, transform 500ms"
        spacing={0}
        maxHeight="200px"
        overflowY="auto">
        {options.length ? (
          options.map((o, i) => (
            <DropdownOptionView
              key={`${i}-${isDropdownOptionItem(o) ? o.id : o.title}`}
              // w='100%'
              option={o}
              field={field}
              index={i}
              selected={selected}
              baseIndex={getBaseIndex(i)}
              focusedIndex={focusedIndex}
              onFocusedElementRef={onFocusedElementRef}
              onChange={v => {
                onChange(v)
              }}
            />
          ))
        ) : (
          <Text color="gray.500" py={2} px={3}>
            {searchQuery ? 'No options matching search' : 'No options'}
          </Text>
        )}
      </VStack>
    </Portal>
  )
}

const NonSearchDropdownButton = forwardRef<
  HTMLButtonElement,
  {
    field: DropdownField
    meta: FieldRenderProps['meta']
    value: string
    handleBlur: () => void
    handleOpen: () => void
    style?: CSSProperties
    disabled?: boolean
    readOnly?: boolean
    onKeyDown?: (e: KeyboardEvent<HTMLButtonElement>) => void
    previewText: string
    flipped?: boolean
    selectedItems?: DropdownOptionItem[]
    size?: InputSize
  }
>(
  (
    {
      field,
      meta,
      value,
      handleBlur,
      handleOpen,
      style,
      disabled,
      readOnly,
      onKeyDown,
      previewText,
      flipped,
      selectedItems,
      size: sizeProp,
    },
    ref,
  ) => {
    const size = useInputSize(sizeProp ?? field?.size)
    const { active } = meta ?? {}
    const { placeholder, options, renderOption } = field ?? {}
    const borderColor = useStatusBorderColor(field, meta)
    const sizeTokens = getInputSizeTokens(size)
    const selectedOption = useMemo<DropdownOptionItem | null>(() => {
      if (!value) return null
      const option = getOptionFromText(options, value)
      return option ?? null
    }, [value, options])

    return (
      <Button
        ref={ref}
        bg="white"
        flex={1}
        // minW='200px'
        fontWeight={400}
        fontSize={sizeTokens.placeholderFontSize}
        variant="outline"
        size="sm"
        fontFamily="hero-new"
        py={renderOption && selectedOption ? 0 : getInputContainerPaddingY(size)}
        pl={renderOption && selectedOption ? 0 : getInputContainerPaddingX(size)}
        pr={getInputContainerPaddingX(size)}
        _hover={{ bg: 'gray.50' }}
        // lineHeight="16px"
        whiteSpace="pre-wrap"
        justifyContent="flex-start"
        textAlign="left"
        height="auto"
        tabIndex={0}
        isDisabled={disabled}
        onKeyDown={onKeyDown}
        onClick={e => {
          e.stopPropagation()
          if (active) {
            handleBlur()
          } else if (!disabled && !readOnly) {
            handleOpen()
          }
        }}
        onFocus={readOnly ? undefined : handleOpen}
        onPointerDown={e => {
          e.preventDefault()
          e.stopPropagation()
        }}
        color={value ? 'blackAlpha.800' : 'blackAlpha.600'}
        borderTopRadius={flipped ? (active ? 0 : 8) : 8}
        borderBottomRadius={flipped ? 8 : active ? 0 : 8}
        transition="all 500ms"
        rightIcon={<ChevronDownIcon />}
        border="0.5px solid"
        borderColor={borderColor}
        style={style}>
        {field.renderOption && selectedOption && !previewText ? (
          <Box minW="0" flex={1}>
            {field.renderOption(selectedOption)}
          </Box>
        ) : selectedItems?.length ? (
          <Flex flex={1} flexWrap="wrap" gap={1} minW={0}>
            {selectedItems.map(item => (
              <Badge key={item.id} colorScheme="green">
                {item.text}
              </Badge>
            ))}
          </Flex>
        ) : (
          <Text
            isTruncated={!field.renderOption}
            minW="0"
            maxW="100%"
            style={baseInputStyle(size, disabled)}
            opacity={value && !previewText ? 1 : 0.7}
            flex={1}>
            {previewText || selectedOption?.text || value || placeholder}
          </Text>
        )}
      </Button>
    )
  },
)

const noneOption: DropdownOptionItem = {
  text: 'None',
  id: 'NULL',
}

const addNoneOption = (field: DropdownField, options: DropdownOption<any>[]) => {
  if (field.withNoneOption) {
    return [...options, noneOption]
  }
  return options
}

export const MenuDropdownInput: InputElement<DropdownField> = forwardRef<
  { focus: () => void },
  InputProps<DropdownField>
>(({ input, field, meta, style, disabled, readOnly, inputStyle, size }, ref) => {
  const { options, searchable, allowMultiple } = field
  const { onChange, value, onBlur, onFocus, name } = input
  const { active } = meta ?? {}
  const { height: screenHeight } = useScreen()

  const [popperPosition, setPopperPosition] = useState<PopperPosition | null>(null)
  const flipped = useMemo(() => {
    if (!popperPosition) return false
    return window.innerHeight - popperPosition.y - popperPosition.height < 150
  }, [popperPosition])
  const dropdownMaxHeight = useMemo(() => {
    if (!popperPosition) return 200
    const available = flipped
      ? popperPosition.y
      : screenHeight - popperPosition.y - popperPosition.height
    return Math.min(available - 20, 200)
  }, [popperPosition, flipped, screenHeight])
  const [searchQuery, setSearchQuery] = useState('')
  const [focusedOptionIndex, setFocusedOptionIndex] = useState<number>(-1)
  const [previewText, setPreviewText] = useState<string>('')

  const valueAsArray = useMemo(() => {
    if (!allowMultiple) return []
    if (!value) return []
    if (!Array.isArray(value)) return []
    return value
  }, [allowMultiple, value])

  const selectedOptionItems = useMemo<DropdownOptionItem[]>(() => {
    if (!allowMultiple || !valueAsArray.length) return []
    const allOpts = getAllOptions(addNoneOption(field, options))
    return valueAsArray
      .map(id => allOpts.find(o => o.id === id))
      .filter((o): o is DropdownOptionItem => o !== undefined)
  }, [allowMultiple, valueAsArray, field, options])

  const valueAsString = useMemo(() => {
    if (allowMultiple) return ''
    if (!value) return ''
    if (typeof value !== 'string') return ''
    return value
  }, [allowMultiple, value])

  const inputRef = useRef<HTMLInputElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const dropdownContainerRef = useRef<HTMLDivElement>(null)
  const focusedElementRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!active) {
      if (field.withNoneOption && valueAsString === 'NULL') {
        setSearchQuery('None')
      } else if (!allowMultiple) {
        setSearchQuery(
          getAllOptions(addNoneOption(field, options)).find(o => o.id === valueAsString)?.text ??
            '',
        )
      }
      setPreviewText('')
    } else {
      setSearchQuery('')
      setPreviewText('')
    }
  }, [valueAsString, options, active, field, allowMultiple])

  const focusSpanRef = useRef<HTMLSpanElement>(null)
  const handleBlur = useCallback(() => {
    const curr = inputRef.current?.value ?? ''
    if (curr) {
      const option = getOptionFromId(options, curr) ?? getOptionFromText(options, curr)
      if (option) {
        onChange(option.id)
      }
    }
    onBlur()
    inputRef.current?.blur()
    buttonRef.current?.blur()
    setSearchQuery('')
    setFocusedOptionIndex(-1)
    setPreviewText('')
    focusSpanRef.current?.focus()
  }, [onChange, options, onBlur])

  const filteredOptions = useMemo(() => {
    if (searchable && searchQuery) {
      return addNoneOption(field, filterOptionsBySearch(options, searchQuery))
    }
    return addNoneOption(field, options)
  }, [searchQuery, options, searchable, field])

  // Get flat list of all selectable options for keyboard navigation
  const flatOptions = useMemo(() => getAllOptions(filteredOptions), [filteredOptions])

  // Reset focused index when options change or dropdown closes
  useEffect(() => {
    if (!active) {
      setFocusedOptionIndex(-1)
      setPreviewText('')
    }
  }, [active])

  useEffect(() => {
    setFocusedOptionIndex(-1)
    setPreviewText('')
  }, [filteredOptions])

  // Update preview text when focused option changes
  useEffect(() => {
    if (focusedOptionIndex >= 0 && focusedOptionIndex < flatOptions.length) {
      const focusedOption = flatOptions[focusedOptionIndex]
      setPreviewText(focusedOption.text)
    } else {
      setPreviewText('')
    }
  }, [focusedOptionIndex, flatOptions])

  // Handle focused element ref callback and auto-scroll
  const handleFocusedElementRef = useCallback(
    (element: HTMLElement | null, index: number) => {
      if (index === focusedOptionIndex) {
        focusedElementRef.current = element
        if (element && dropdownContainerRef.current) {
          // Use scrollIntoView with smooth behavior
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'nearest',
          })
        }
      }
    },
    [focusedOptionIndex],
  )

  const handleSelect = useCallback(
    (selected: string | undefined) => {
      if (allowMultiple) {
        if (!selected) {
          onChange([])
          return
        }
        if (valueAsArray.includes(selected)) {
          onChange(valueAsArray.filter(v => v !== selected))
        } else {
          onChange([...(valueAsArray ?? []), selected])
        }
      } else {
        if (!selected) {
          onChange(undefined)
        } else onChange(selected)
      }
    },
    [allowMultiple, valueAsArray, onChange],
  )

  const containerRef = useRef<HTMLDivElement>(null)

  const handleOpen = useCallback(() => {
    if (!containerRef.current) return
    if (disabled || readOnly) return
    const { x, y, height, width } = containerRef.current.getBoundingClientRect()
    setPopperPosition({
      x,
      y,
      height,
      width,
    })
    onFocus()
    setTimeout(() => {
      if (searchable) {
        inputRef.current?.focus()
      } else {
        buttonRef.current?.focus()
      }
    }, 50)
  }, [onFocus, disabled, readOnly, searchable])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement | HTMLButtonElement>) => {
      if (!active) {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
          e.preventDefault()
          if (!disabled && !readOnly) {
            handleOpen()
          }
        }
        return
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setFocusedOptionIndex(prev => {
            if (flatOptions.length === 0) return -1
            const nextIndex = prev + 1
            return nextIndex >= flatOptions.length ? 0 : nextIndex
          })
          break
        case 'ArrowUp':
          e.preventDefault()
          setFocusedOptionIndex(prev => {
            if (flatOptions.length === 0) return -1
            const nextIndex = prev - 1
            return nextIndex < 0 ? flatOptions.length - 1 : nextIndex
          })
          break
        case 'Enter':
          e.preventDefault()
          if (focusedOptionIndex >= 0 && focusedOptionIndex < flatOptions.length) {
            const selectedOption = flatOptions[focusedOptionIndex]
            handleSelect(selectedOption.id)
            handleBlur()
          }
          break
        case 'Escape':
          e.preventDefault()
          e.stopPropagation()
          handleBlur()
          break
        // case 'Tab':
        //   handleBlur()
        //   break
      }
    },
    [
      active,
      focusedOptionIndex,
      flatOptions,
      handleSelect,
      handleBlur,
      disabled,
      readOnly,
      handleOpen,
    ],
  )

  const handleContentResize = useCallback(
    (e: ResizeObserverEntry) => {
      if (!active) return
      const { x, y, height, width } = e.target.getBoundingClientRect()
      setPopperPosition({
        x,
        y,
        height,
        width,
      })
    },
    [active],
  )
  useResizeObserver(containerRef, handleContentResize)

  useEffect(() => {
    if (!active) return () => {}
    if (!containerRef.current) return () => {}
    const scrollableParent = getScrollableParent(containerRef.current)
    if (!scrollableParent) return () => {}
    const handleScroll = () => {
      if (!containerRef.current) return
      const { x, y, height, width } = containerRef.current.getBoundingClientRect()
      setPopperPosition({
        x,
        y,
        height,
        width,
      })
    }
    scrollableParent.addEventListener('scroll', handleScroll)
    return () => {
      scrollableParent.removeEventListener('scroll', handleScroll)
    }
  }, [active])

  useImperativeHandle(ref, () => ({
    focus: handleOpen,
    blur: handleBlur,
  }))

  const getItemKey = useCallback(
    (o: DropdownOption<any>, index: number) => {
      if (!isDropdownOptionItem(o)) return `${index}-${o.title}`
      return `${index}-${field.getKey ? field.getKey(o.id) : o.id}`
    },
    [field],
  )

  // Calculate base index for keyboard navigation based on filtered options
  const getBaseIndex = useCallback(
    (localIndex: number) => {
      let baseIndex = 0
      for (let i = 0; i < localIndex; i++) {
        const opt = filteredOptions[i]
        if (isDropdownOptionItem(opt)) {
          baseIndex++
        } else {
          baseIndex += opt.options.length
        }
      }
      return baseIndex
    },
    [filteredOptions],
  )

  // Get display value for the input field
  const displayValue = useMemo(() => {
    if (searchable && active) {
      return previewText || searchQuery
    }
    if (field.withNoneOption && value === 'NULL') {
      return 'None'
    }
    const withNone = addNoneOption(field, options)
    if (Array.isArray(value)) {
      return value.map(v => getAllOptions(withNone).find(o => o.id === v)?.text ?? '').join(', ')
    }
    return getAllOptions(withNone).find(o => o.id === value)?.text ?? ''
  }, [searchable, active, previewText, searchQuery, field, value, options])

  // // Get display value for non-searchable button
  // const buttonDisplayValue = useMemo((): string => {
  //   if (active && previewText) {
  //     return previewText
  //   }
  //   if (field.withNoneOption && value === 'NULL') {
  //     return 'None'
  //   }
  //   const selectedOption = getAllOptions(addNoneOption(field, options)).find(o => o.id === value)
  //   if (selectedOption?.text) {
  //     return selectedOption.text
  //   }
  //   if (field.placeholder) {
  //     return field.placeholder
  //   }
  //   return ''
  // }, [active, previewText, field, value, options])

  return (
    <Flex
      data-child-name={name}
      borderRadius={6}
      pointerEvents={disabled ? 'none' : 'auto'}
      minW="0"
      w="100%">
      <HStack
        onBlur={handleBlur}
        spacing={0}
        ref={containerRef}
        minW="0"
        width="100%"
        flex={1}
        position="relative"
        align="center">
        <HStack w="100%" flex={1} spacing={2}>
          {searchable && !disabled && !readOnly ? (
            <DropdownSearchBar
              onBlur={handleBlur}
              field={{
                ...field,
                placeholder: previewText || field.placeholder,
              }}
              style={style}
              disabled={disabled}
              name={name}
              meta={meta}
              size={size}
              onFocus={handleOpen}
              searchQuery={searchQuery}
              ref={inputRef}
              onKeyDown={handleKeyDown}
              onSearchQueryChange={q => {
                setSearchQuery(q)
                if (!active) {
                  handleBlur()
                }
              }}
              previewText={previewText}
              value={displayValue}
              fieldActive={active}
              flipped={flipped}
              inputStyle={inputStyle}
              selectedItems={selectedOptionItems}
            />
          ) : (
            <NonSearchDropdownButton
              ref={buttonRef}
              field={field}
              meta={meta}
              value={displayValue}
              previewText={previewText}
              style={style}
              size={size}
              handleBlur={handleBlur}
              handleOpen={handleOpen}
              disabled={disabled}
              readOnly={readOnly}
              onKeyDown={handleKeyDown}
              flipped={flipped}
              selectedItems={selectedOptionItems}
            />
          )}
          {field.optional && value && !disabled && !readOnly ? (
            <Center>
              <Tooltip label="Clear" placement="top" hasArrow>
                <IconButton
                  size="xs"
                  width="20px"
                  height="20px"
                  minW="0"
                  borderRadius="full"
                  color="gray.600"
                  // top='2rem'
                  bg="gray.50"
                  // zIndex={}
                  border="1px solid #cdcdcd"
                  // boxShadow='md'
                  aria-label="clear"
                  onClick={e => {
                    e.stopPropagation()
                    if (inputRef.current) inputRef.current.value = ''
                    input.onChange(undefined)
                    handleBlur()
                  }}
                  icon={<CloseIcon w={2} />}
                />
              </Tooltip>
            </Center>
          ) : null}
        </HStack>
        <Portal>
          {/* <Box zIndex={2} pointerEvents='none' top={0} left={0} w="100%" h="100%"> */}
          <VStack
            ref={dropdownContainerRef}
            position="fixed"
            width={`${popperPosition?.width ?? 0}px`}
            // top={`${(popperPosition?.y ?? 0) + (popperPosition?.height ?? 0)}px`}
            // left={`${popperPosition?.x ?? 0}px`}
            top={0}
            left={0}
            zIndex={30}
            py={0}
            bg="white"
            align="flex-start"
            onClick={e => {
              e.stopPropagation()
            }}
            boxShadow="0 0 4px #00000077"
            minW="300px"
            borderBottomRadius={flipped ? 0 : 6}
            borderTopRadius={flipped ? 6 : 0}
            opacity={active ? 1 : 0}
            pointerEvents={active ? 'auto' : 'none'}
            // transform={active ? 'translateY(0)' : 'translateY(-5px)'}
            transform={
              popperPosition
                ? flipped
                  ? `translate(${popperPosition.x}px, ${popperPosition.y}px) translateY(-100%)`
                  : `translate(${popperPosition.x}px, ${popperPosition.y + popperPosition.height}px)`
                : 'translate(0, 0)'
            }
            transition="opacity 300ms"
            spacing={0}
            divider={<Divider />}
            maxHeight={`${dropdownMaxHeight}px`}
            overflowY="auto">
            {filteredOptions.length ? (
              filteredOptions.map((o, i) => (
                <DropdownOptionView
                  key={getItemKey(o, i)}
                  // w='100%'
                  option={o}
                  field={field}
                  index={i}
                  selected={allowMultiple ? valueAsArray : valueAsString}
                  baseIndex={getBaseIndex(i)}
                  focusedIndex={focusedOptionIndex}
                  onFocusedElementRef={handleFocusedElementRef}
                  onChange={v => {
                    handleSelect(v)
                    if (!allowMultiple) handleBlur()
                  }}
                />
              ))
            ) : (
              <Text color="gray.500" py={2} px={3}>
                {searchQuery ? 'No options matching search' : 'No options'}
              </Text>
            )}
          </VStack>
          {/* </Box> */}
        </Portal>
      </HStack>
      {active ? <span tabIndex={0} ref={focusSpanRef} onFocus={e => e.stopPropagation()} /> : null}
    </Flex>
  )
})
